/**
 * Videos page – Thursday Jam's own video gallery.
 * Shows band performances and music videos in a 2-column grid.
 * Clicking a thumbnail opens a lightbox dialog with a YouTube embed.
 */
import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { Play, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import Navigation from "@/components/Navigation";
import BlurImage from "@/components/BlurImage";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import Footer from "@/components/Footer";
import GrainOverlay from "@/components/GrainOverlay";
import PageFrame from "@/components/PageFrame";
import VariableHeadline from "@/components/VariableHeadline";
import hero03 from "@/assets/hero/hero-03.jpg?format=webp&quality=85";

const videos = [
  {
    id: "1",
    title: "Live at Slaughterhouse Berlin",
    thumbnail: "https://img.youtube.com/vi/SLZ-9qzpUYg/maxresdefault.jpg",
    youtubeId: "SLZ-9qzpUYg",
  },
  {
    id: "2",
    title: "The Rehearsal Room Session",
    thumbnail: "https://img.youtube.com/vi/SINSzSFaKeM/maxresdefault.jpg",
    youtubeId: "SINSzSFaKeM",
  },
  {
    id: "3",
    title: "I Was Supposed to Die Today (Visualizer)",
    thumbnail: "https://img.youtube.com/vi/cn-lINjyaKo/maxresdefault.jpg",
    youtubeId: "cn-lINjyaKo",
  },
  {
    id: "4",
    title: "Reaching for the Stars",
    thumbnail: "https://img.youtube.com/vi/WimGsGrPUbM/maxresdefault.jpg",
    youtubeId: "WimGsGrPUbM",
  },
];

const Videos = () => {
  useDocumentMeta({
    title: "Live Footage – Thursday Jam",
    description: "Watch Thursday Jam live performances, rehearsal sessions, and music videos. A Berlin-based family band playing soulful blues rock rooted in classic guitar music.",
    canonical: "https://thursdayjam.rocks/videos",
    ogImage: hero03,
  });
  const [selectedVideo, setSelectedVideo] = useState<typeof videos[0] | null>(null);

  return (
    <main id="main-content" className="relative min-h-screen bg-background">
      <PageFrame />
      <GrainOverlay />
      <Navigation variant="about" />

      {/* Hero section */}
      <section className="relative pt-32 pb-16 px-6 md:px-24 lg:px-32">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="mb-6 h-px w-24 origin-left bg-foreground/30"
          />
          <VariableHeadline 
            as="h1" 
            pattern="edges"
            className="text-5xl md:text-7xl lg:text-8xl mb-4"
          >
            Live Footage
          </VariableHeadline>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="font-body text-lg text-muted-foreground"
          >
            <span className="text-base">Watch us in action</span>
          </motion.p>
        </div>
      </section>

      {/* Video Grid */}
      <section className="px-6 md:px-24 lg:px-32 pb-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid gap-8 md:grid-cols-2">
            {videos.map((video, index) => (
              <motion.div
                key={video.id}
                role="button"
                tabIndex={0}
                aria-label={`Play video: ${video.title}`}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedVideo(video); } }}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 + index * 0.15 }}
                className="group relative cursor-pointer overflow-hidden rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                onClick={() => setSelectedVideo(video)}
              >
                {/* Thumbnail */}
                <div className="relative aspect-video overflow-hidden">
                  <BlurImage
                    src={video.thumbnail}
                    alt={video.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-background/40 transition-opacity group-hover:bg-background/60" />

                  {/* Play Button */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/90 text-primary-foreground transition-transform group-hover:scale-110">
                      <Play className="ml-1 h-8 w-8" fill="currentColor" />
                    </div>
                  </div>
                </div>

                {/* Title */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background via-background/80 to-transparent p-4 pt-12">
                  <h3 className="font-display text-xl tracking-wide text-foreground">
                    {video.title}
                  </h3>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {/* Video Modal */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="max-w-4xl border-border bg-background p-0">
          <VisuallyHidden>
            <DialogTitle>{selectedVideo?.title || "Video"}</DialogTitle>
          </VisuallyHidden>
          <button
            onClick={() => setSelectedVideo(null)}
            className="absolute -right-2 -top-2 z-50 flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-110"
          >
            <X size={20} />
          </button>
          {selectedVideo && (
            <div className="aspect-video w-full">
              <iframe
                src={`https://www.youtube.com/embed/${selectedVideo.youtubeId}?autoplay=1`}
                className="h-full w-full rounded-lg"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                title={selectedVideo.title}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default Videos;
